<template>
  <Dialog
    v-model:visible="visible"
    modal
    :style="{ width: '30rem' }"
    :breakpoints="{ '640px': '94vw' }"
    class="rss-dialog"
  >
    <template #header>
      <div class="rss-header">
        <div class="rss-header__icon"><i class="pi pi-calendar-clock"></i></div>
        <div class="rss-header__text">
          <h2 class="rss-header__title">موعد فتح الحجز</h2>
          <p class="rss-header__subtitle" v-if="academicYear">{{ academicYear.name }}</p>
        </div>
      </div>
    </template>

    <p class="rss-desc">
      حدد التاريخ والوقت الذي يفتح فيه باب الحجز لهذه السنة الدراسية تحديداً (بعد الموعد العام لفتح الموقع).
      اترك الحقلين فارغين إذا كنت تريد أن يفتح الحجز لهذه السنة فور فتح الموقع مباشرةً.
    </p>

    <div class="row g-3">
      <div class="col-6">
        <div class="form-field">
          <label class="field-label">التاريخ</label>
          <DatePicker v-model="date" showIcon fluid iconDisplay="input" class="w-100" />
        </div>
      </div>
      <div class="col-6">
        <div class="form-field">
          <label class="field-label">الوقت</label>
          <DatePicker v-model="time" showIcon fluid iconDisplay="input" hourFormat="12" timeOnly class="w-100">
            <template #inputicon="slotProps">
              <i class="pi pi-clock" @click="slotProps.clickCallback" />
            </template>
          </DatePicker>
        </div>
      </div>
    </div>

    <button
      type="button"
      class="rss-clear-btn"
      v-if="date || time"
      @click="clearSchedule"
    >
      <i class="pi pi-times"></i> إلغاء الموعد (فتح الحجز فوراً)
    </button>

    <template #footer>
      <div class="rss-footer">
        <button type="button" class="rss-btn rss-btn--secondary" @click="visible = false">
          إلغاء
        </button>
        <button
          type="button"
          class="rss-btn rss-btn--primary"
          :disabled="saving || (Boolean(date) !== Boolean(time))"
          @click="save"
        >
          <i class="pi pi-spin pi-spinner-dotted" v-if="saving"></i>
          <i class="pi pi-check" v-else></i>
          حفظ الموعد
        </button>
      </div>
    </template>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import DatePicker from 'primevue/datepicker';
import Swal from 'sweetalert2';
import { useGradeStore } from '@/stores/useGradeStore.js';

export default {
  name: 'ReservationScheduleModal',
  components: { Dialog, DatePicker },
  props: {
    modelValue: { type: Boolean, default: false },
    academicYear: { type: Object, default: null },
  },
  emits: ['update:modelValue', 'saved'],
  data() {
    return {
      date: null,
      time: null,
      saving: false,
    };
  },
  computed: {
    gradeStore() {
      return useGradeStore();
    },
    visible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
  },
  watch: {
    modelValue(isOpen) {
      if (!isOpen) return;
      const raw = this.academicYear?.reservation_start_at;
      if (raw) {
        // Backend stores a naive "Y-m-d H:i:s" string (Cairo local time convention,
        // same as the global website_start timer) — parse it as local time.
        const iso = raw.replace(' ', 'T');
        this.date = new Date(iso);
        this.time = new Date(iso);
      } else {
        this.date = null;
        this.time = null;
      }
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      const month = '' + (d.getMonth() + 1);
      const day = '' + d.getDate();
      const year = d.getFullYear();
      return [year, month.padStart(2, '0'), day.padStart(2, '0')].join('-');
    },
    formatTime(time) {
      if (!time) return '';
      const d = new Date(time);
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}:00`;
    },
    clearSchedule() {
      this.date = null;
      this.time = null;
    },
    async save() {
      this.saving = true;
      try {
        const reservationStartAt = (this.date && this.time)
          ? `${this.formatDate(this.date)} ${this.formatTime(this.time)}`
          : null;
        const saved = await this.gradeStore.updateReservationSchedule(this.academicYear.id, reservationStartAt);
        this.$emit('saved', saved);
        Swal.fire({ icon: 'success', title: 'تم حفظ موعد فتح الحجز', timer: 1200, showConfirmButton: false });
        this.visible = false;
      } catch {
        Swal.fire({ icon: 'error', title: 'حدث خطأ أثناء الحفظ', timer: 2000, showConfirmButton: false });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.rss-header {
  display: flex;
  align-items: center;
  gap: 0.875rem;
}
.rss-header__icon {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: var(--radius-lg);
  background: var(--primary-light);
  color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
}
.rss-header__title {
  font-size: 1.125rem;
  font-weight: 800;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.25;
}
.rss-header__subtitle {
  font-size: 0.8125rem;
  color: var(--primary-color);
  font-weight: 600;
  margin: 0.1875rem 0 0;
}
.rss-desc {
  font-size: 0.8125rem;
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0 0 1.25rem;
}
.field-label {
  display: block;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.375rem;
}
.rss-clear-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.875rem;
  background: transparent;
  border: none;
  color: var(--color-danger, #ef4444);
  font-size: 0.8125rem;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
}
.rss-footer {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.75rem;
  width: 100%;
}
.rss-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem 1.875rem;
  border-radius: var(--radius-md);
  font-size: 0.875rem;
  font-weight: 700;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all var(--ease-base);
}
.rss-btn--primary {
  background: var(--primary-color);
  color: #fff;
}
.rss-btn--primary:hover:not(:disabled) {
  background: var(--primary-dark);
  box-shadow: var(--shadow-primary);
  transform: translateY(-1px);
}
.rss-btn--primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}
.rss-btn--secondary {
  background: transparent;
  border-color: var(--surface-border);
  color: var(--text-secondary);
}
.rss-btn--secondary:hover {
  border-color: var(--primary-color);
  color: var(--text-primary);
}
</style>
